import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";

export const inputCls =
  "w-full px-3.5 py-2.5 border border-neutral-300 rounded-md bg-surface text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-[border-color,box-shadow]";

export function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block eyebrow text-neutral-600 mb-2">{label}</span>
      {children}
      {hint && <span className="block mt-1.5 text-xs text-neutral-500">{hint}</span>}
    </label>
  );
}

export function TextInput({ label, value, onChange, testid, hint, type = "text", className, ...rest }) {
  return (
    <Field label={label} hint={hint} className={className}>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={inputCls}
        {...rest}
      />
    </Field>
  );
}

export function TextArea({ label, value, onChange, testid, hint, rows = 4, className }) {
  return (
    <Field label={label} hint={hint} className={className}>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={`${inputCls} resize-y leading-relaxed`}
      />
    </Field>
  );
}

export function SelectInput({ label, value, onChange, options, testid, placeholder = "Select…", className }) {
  return (
    <Field label={label} className={className}>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

export function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="w-4 h-4 accent-indigo-800"
      />
      <span className="text-sm text-neutral-800">{label}</span>
    </label>
  );
}

export function LinesInput({ label, value = [], onChange, testid, rows = 4 }) {
  return (
    <TextArea
      label={label}
      rows={rows}
      hint="One item per line"
      value={value.join("\n")}
      onChange={(v) => onChange(v.split("\n"))}
      testid={testid}
    />
  );
}

export function RowControls({ index, total, onMove, onRemove, testid }) {
  const btn = "p-1.5 rounded-md text-neutral-500 hover:text-indigo-800 hover:bg-indigo-50 disabled:opacity-30 transition-colors";
  return (
    <div className="flex items-center gap-1">
      <button type="button" className={btn} disabled={index === 0} onClick={() => onMove(index, -1)} aria-label="Move up" data-testid={`${testid}-up-${index}`}>
        <ArrowUp size={14} />
      </button>
      <button type="button" className={btn} disabled={index === total - 1} onClick={() => onMove(index, 1)} aria-label="Move down" data-testid={`${testid}-down-${index}`}>
        <ArrowDown size={14} />
      </button>
      <button type="button" className={`${btn} hover:text-red-700 hover:bg-red-50`} onClick={() => onRemove(index)} aria-label="Remove" data-testid={`${testid}-remove-${index}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export function AddRowButton({ onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-800 hover:text-indigo-950 transition-colors"
    >
      <Plus size={15} /> {label}
    </button>
  );
}

export function useRows(value, onChange) {
  const edit = (i, patch) => onChange(value.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const next = [...value];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange(next);
  };
  return { edit, remove, move };
}
